import React, { useState, useEffect, FormEvent } from 'react';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DatePicker from '@mui/lab/DatePicker';
import {
  Box,
  TextField,
  Grid,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  SelectChangeEvent,
  TextFieldProps,
} from '@mui/material';
import FormHelperText from '@mui/material/FormHelperText';
import Autocomplete from '@mui/material/Autocomplete';
import { Dialog, toast } from '@cincoders/cinnamon';
import { CovenantService } from '../../services/CovenantService';
import CurrencyInput from '../../components/Input/CurrencyInput';
import useDebounce from '../../hooks/useDebounce';
import { CoordenadorContainer } from './styles';
import { PageForm, FormButton } from '../../components/FormLayout/FormLayout.styles';
import { CovenantDTO, PersonInterface } from '../../types/Convenant.d';

const FormCovenant: React.FC = () => {
  const [covenant, setCovenant] = useState<CovenantDTO>({
    id: null,
    companyName: '',
    covenantNumber: '',
    startDate: null,
    endDate: null,
    coordinator: {
      id: 0,
      siape: '',
      name: '',
    },
    financier: '',
    totalValue: null,
    fadeValue: null,
    cinValue: null,
    centralAdmValue: null,
  });

  const [responseCovenant, setResponseCovenant] = useState<CovenantDTO>({
    id: null,
    companyName: '',
    covenantNumber: covenant.covenantNumber,
    startDate: null,
    endDate: null,
    financier: '',
    coordinator: {
      id: 0,
      siape: '',
      name: '',
    },
    totalValue: null,
    fadeValue: null,
    cinValue: null,
    centralAdmValue: null,
  });

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [siapeInvalid, setSiapeInvalid] = useState<string | null>(null);
  const [nameInvalid, setNameInvalid] = useState<string | null>(null);
  const [startDateInvalid, setStartDateInvalid] = useState<string | null>(null);
  const [endDateInvalid, setEndDateInvalid] = useState<string | null>(null);
  const [visibility, setVisibility] = useState<boolean>(false);
  const [editVisibility, setEditVisibility] = useState<boolean>(false);
  const debouncedCovenantNumber = useDebounce<string>(covenant.covenantNumber, 2000);
  const debouncedPersonSiape = useDebounce<string>(covenant.coordinator.siape, 2000);
  const debouncedPersonName = useDebounce<string>(covenant.coordinator.name, 2000);
  const [persons, setPersons] = useState<Array<PersonInterface>>([]);

  useEffect(() => {
    async function findByNumber() {
      if (debouncedCovenantNumber !== '') {
        const response = await CovenantService.findByNumber(debouncedCovenantNumber);
        if (response.data.length === 1) {
          setResponseCovenant(response.data[0]);
          setEditVisibility(true);
          setIsEditing(true);
        } else if (response.data.length === 0) {
          if (isEditing) {
            setVisibility(true);
          }
          setIsEditing(false);
        }
      }
    }
    findByNumber();
  }, [debouncedCovenantNumber, isEditing]);

  useEffect(() => {
    async function callAPI() {
      if (debouncedPersonSiape !== '') {
        const response = await CovenantService.findPerson(debouncedPersonSiape, undefined);
        if (response.status === 200) {
          const coordId = response.data[0].id;
          setSiapeInvalid('');
          setCovenant({
            ...covenant,
            coordinator: {
              id: coordId,
              siape: response.data[0].siape,
              name: response.data[0].name,
            },
          });
        } else if (response.status === 404) {
          setSiapeInvalid('Siape not found!');
        }
      }
    }
    callAPI();
  }, [covenant, debouncedPersonSiape]);

  useEffect(() => {
    async function callAPI() {
      if (debouncedPersonName !== '') {
        const response = await CovenantService.findPerson(undefined, debouncedPersonName);
        if (response.status === 200) {
          setNameInvalid('');
          const personsList: Array<PersonInterface> = [];
          for (let i = 0; i < response.data.length; i += 1) {
            const { id, name, siape } = response.data[i];
            personsList.push({ id, name, siape });
          }
          setPersons(personsList);
          if (personsList.length === 1) {
            const coordId = response.data[0].id;
            setCovenant({
              ...covenant,
              coordinator: {
                id: coordId,
                siape: response.data[0].siape,
                name: response.data[0].name,
              },
            });
          }
        } else if (response.status === 404) {
          setNameInvalid('Name not found!');
        }
      }
    }
    callAPI();
  }, [covenant, debouncedPersonName]);

  async function createCovenant() {
    const response = await CovenantService.createCovenant(covenant);
    if (response.status === 201) {
      toast.success('Covênio criado com sucesso');
    }
  }

  async function updateCovenant() {
    const response = await CovenantService.updateCovenant(covenant);
    if (response.status === 200) {
      toast.success('Covênio editado com sucesso');
    }
  }

  function clearCovenant(): void {
    setCovenant({
      id: null,
      companyName: '',
      covenantNumber: covenant.covenantNumber,
      startDate: null,
      endDate: null,
      financier: '',
      coordinator: {
        id: 0,
        siape: '',
        name: '',
      },
      totalValue: null,
      fadeValue: null,
      cinValue: null,
      centralAdmValue: null,
    });
  }

  function loadCovenant(): void {
    setCovenant(responseCovenant);
  }

  function dateValidation(date: Date | null, setValidationState: React.Dispatch<React.SetStateAction<string | null>>) {
    if (date !== null) {
      if (date.getFullYear()) {
        setValidationState(null);
      } else {
        setValidationState('Invalid Date');
      }
    }
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (isEditing) {
      await updateCovenant();
    } else {
      await createCovenant();
    }
  }

  return (
    <>
      <PageForm>
        <form onSubmit={e => handleSubmit(e)}>
          <Dialog
            visibility={visibility}
            setVisibility={setVisibility}
            acceptFunction={() => {
              clearCovenant();
              setVisibility(false);
            }}
            rejectFunction={() => {
              setVisibility(false);
            }}
            type='decision'
            title='Convênio não encontrado, deseja limpar o formulário? Todas as informações digitadas serão perdidas.'
          >
            {' '}
          </Dialog>
          <Dialog
            visibility={editVisibility}
            setVisibility={setEditVisibility}
            acceptFunction={() => {
              loadCovenant();
              setEditVisibility(false);
            }}
            rejectFunction={() => {
              setEditVisibility(false);
            }}
            type='decision'
            title={
              `Deseja carregar o convênio ${responseCovenant?.covenantNumber} - ${responseCovenant?.companyName}?` +
              'Todas os campos digitados serão perdidos.'
            }
          >
            {' '}
          </Dialog>
          <h2>{isEditing ? 'Edição de Convênio' : 'Cadastro de Convênio'}</h2>
          <Box sx={{ flexGrow: 1 }} paddingTop={2}>
            <Grid container spacing={2}>
              <Grid item xs={5} sm={3} md={3} lg={3}>
                <TextField
                  fullWidth
                  required
                  label='Número Convênio'
                  value={covenant?.covenantNumber}
                  onChange={e => {
                    setCovenant({
                      ...covenant,
                      covenantNumber: e.target.value,
                    });
                  }}
                />
              </Grid>
              <Grid item xs={7} sm={6}>
                <TextField
                  required
                  fullWidth
                  label='Empresa'
                  value={covenant?.companyName}
                  onChange={e => {
                    setCovenant({ ...covenant, companyName: e.target.value });
                  }}
                />
              </Grid>
              <Grid item xs>
                <FormControl fullWidth sx={{ minWidth: 100 }}>
                  <InputLabel id='financier'>financier</InputLabel>
                  <Select
                    sx={{ margin: 0 }}
                    fullWidth
                    labelId='Financier'
                    id='financier'
                    value={covenant.financier || ''}
                    onChange={(event: SelectChangeEvent) => {
                      setCovenant({
                        ...covenant,
                        financier: event.target.value,
                      });
                    }}
                    label='financier'
                  >
                    <MenuItem value='Público'>Publico</MenuItem>
                    <MenuItem value='Privado'>Privado</MenuItem>
                    <MenuItem value='TED'>TED (Termo de Execução Descentralizada)</MenuItem>
                    <MenuItem value='UFPE'>UFPE</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ flexGrow: 1, marginTop: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4} md={3}>
                  <DatePicker
                    label='Data Início'
                    inputFormat='dd/MM/yyyy'
                    value={covenant.startDate}
                    onChange={(newValue: Date | null) => {
                      dateValidation(newValue, setStartDateInvalid);
                      setCovenant({ ...covenant, startDate: newValue });
                      if (endDateInvalid === null || endDateInvalid === 'End date must be longer than start date.') {
                        if (covenant.endDate !== null && newValue !== null) {
                          if (newValue.getTime() > covenant.endDate.getTime()) {
                            setEndDateInvalid('End date must be longer then start date.');
                          } else {
                            setEndDateInvalid(null);
                          }
                        }
                      }
                    }}
                    renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => <TextField {...params} />}
                  />
                  <FormHelperText error>{startDateInvalid}</FormHelperText>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <DatePicker
                    label='Data Final'
                    value={covenant.endDate}
                    inputFormat='dd/MM/yyyy'
                    onChange={(newValue: Date | null) => {
                      dateValidation(newValue, setEndDateInvalid);
                      setCovenant({ ...covenant, endDate: newValue });
                      if (endDateInvalid === null || endDateInvalid === 'End date must be longer then start date.') {
                        if (covenant.startDate !== null && newValue !== null) {
                          if (newValue.getTime() < covenant.startDate.getTime()) {
                            setEndDateInvalid('End date must be longer then start date.');
                          } else {
                            setEndDateInvalid(null);
                          }
                        }
                      }
                    }}
                    renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => <TextField {...params} />}
                  />
                  <FormHelperText error>{endDateInvalid}</FormHelperText>
                </Grid>
              </Grid>
            </Box>
          </LocalizationProvider>
          <CoordenadorContainer>
            <Box sx={{ flexGrow: 1 }}>
              <p>Coordernador</p>
              <Grid container spacing={2}>
                <Grid item xs={6} md={4}>
                  <TextField
                    error={!!siapeInvalid}
                    helperText={siapeInvalid}
                    fullWidth
                    required
                    label='SIAPE'
                    value={covenant.coordinator.siape}
                    onChange={e => {
                      setCovenant({
                        ...covenant,
                        coordinator: {
                          ...covenant.coordinator,
                          siape: e.target.value,
                        },
                      });
                    }}
                  />
                </Grid>
                <Grid item xs={6} md={8}>
                  <Autocomplete
                    freeSolo
                    id='free-solo-2-demo'
                    options={persons.map(person => `${person.siape} - ${person.name}`)}
                    value={covenant.coordinator.name}
                    onChange={(event, newValue) => {
                      let nameValue = '';
                      let cont = 10;
                      if (newValue !== null) {
                        while (cont < newValue.length) {
                          nameValue += newValue[cont];
                          cont += 1;
                        }
                        setCovenant({
                          ...covenant,
                          coordinator: {
                            ...covenant.coordinator,
                            name: nameValue,
                          },
                        });
                      }
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        error={!!nameInvalid}
                        fullWidth
                        required
                        label='Nome'
                        helperText={nameInvalid}
                        value={covenant.coordinator.name}
                        onChange={e => {
                          setCovenant({
                            ...covenant,
                            coordinator: {
                              ...covenant.coordinator,
                              name: e.target.value,
                            },
                          });
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          </CoordenadorContainer>
          <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <CurrencyInput
                  label='Valor Total'
                  value={covenant.totalValue}
                  onChange={e => {
                    if (e.target.value === '') {
                      setCovenant({ ...covenant, totalValue: null });
                    } else {
                      const newValue: number = +e.target.value;
                      setCovenant({ ...covenant, totalValue: newValue });
                    }
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <CurrencyInput
                  label='Valor FADE'
                  value={covenant.fadeValue}
                  onChange={e => {
                    if (e.target.value === '') {
                      setCovenant({ ...covenant, fadeValue: null });
                    } else {
                      const newValue: number = +e.target.value;
                      setCovenant({ ...covenant, fadeValue: newValue });
                    }
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <CurrencyInput
                  label='Valor CIn'
                  value={covenant.cinValue}
                  onChange={e => {
                    if (e.target.value === '') {
                      setCovenant({ ...covenant, cinValue: null });
                    } else {
                      const newValue: number = +e.target.value;
                      setCovenant({ ...covenant, cinValue: newValue });
                    }
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <CurrencyInput
                  label='Valor Adm Central'
                  value={covenant.centralAdmValue}
                  onChange={e => {
                    if (e.target.value === '') {
                      setCovenant({ ...covenant, centralAdmValue: null });
                    } else {
                      const newValue: number = +e.target.value;
                      setCovenant({ ...covenant, centralAdmValue: newValue });
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
          <hr />

          <FormButton
            type='submit'
            variant='contained'
            disabled={!!startDateInvalid || !!endDateInvalid || !!siapeInvalid || !!nameInvalid}
          >
            Salvar
          </FormButton>
        </form>
      </PageForm>
    </>
  );
};

export default FormCovenant;
